import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLocation, Link } from "wouter";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { authLogin, homeForRoles, ApiError } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { setToken, setRefreshToken } from "@/lib/auth";

const formSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});

// Redirection rôle-aware après login : déléguée à homeForRoles() (lib/api),
// qui lit le TABLEAU roles[] renvoyé par l'auth-service et applique la priorité
// sedec > economat > ecole > parent.

export default function Login() {
  const [, setLocation] = useLocation();
  const [pending, setPending] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setPending(true);
    try {
      const res = await authLogin(values);
      setToken(res.accessToken);
      // Refresh silencieux : on stocke aussi le refresh token (renouvelle
      // l'access token expiré sans déconnecter l'utilisateur — cf. api.ts).
      if (res.refreshToken) setRefreshToken(res.refreshToken);
      toast.success("Connexion réussie");
      // Rechargement complet (pas setLocation) : force la relecture du token →
      // l'entête (useCurrentUser) et le routing rôle-aware repartent d'un état frais.
      window.location.assign(homeForRoles(res.user?.roles));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
        setAuthUnavailable(true);
      } else {
        toast.error(friendlyError(err));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-3xl p-8 border border-orange-50 shadow-sm">
        <h1 className="text-2xl font-display font-bold text-navy mb-6">
          Connexion
        </h1>

        {authUnavailable ? (
          <div className="p-4 rounded-xl bg-gold/10 border border-gold/30 text-sm text-navy">
            <p className="font-bold mb-1">Connexion bientôt disponible</p>
            <p className="text-navy/70">
              Votre reçu reste accessible via le lien envoyé par email/SMS après
              chaque paiement.
            </p>
          </div>
        ) : (
          <>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mot de passe</FormLabel>
                      <FormControl>
                        <PasswordInput {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy/90 text-white rounded-xl mt-4"
                  disabled={pending}
                >
                  {pending ? "Connexion…" : "Se connecter"}
                </Button>
              </form>
            </Form>
            <div className="mt-6 text-center text-sm text-navy/70">
              Pas encore de compte ?{" "}
              <Link
                href="/auth/register"
                className="text-coral font-bold hover:underline"
              >
                S'inscrire
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
